import { KdsDropfile } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useContext, useState } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { FileContext } from "@/components/FileUploadAsync/FileUploadAsync.context";
import { SIZE_5G } from "@/constants/fileSize.constants";
import { getDropfileErrorMessage } from "@/helpers/forms";
import { useDirectUpload } from "@/hooks/useDirectUpload";

import { type InferredModalAddExtraAttachmentSchema } from "../../ModalAddExtraAttachment.schema";

export const ExtraAttachmentsInput = () => {
  const methods = useFormContext<InferredModalAddExtraAttachmentSchema>();
  const { fields, append, remove } = useFieldArray({
    name: "extraAttachments",
  });
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState<number | boolean>(false);

  const { pushFile, setFileUploadState } = useContext(FileContext);
  const { uploadFile, createFileUploadUrl } = useDirectUpload();

  const handleUpload = async (file: File) => {
    const uploadDetails = await createFileUploadUrl(
      "sandboxItem.extraAttachments",
      file
    );
    const newSignedId = uploadDetails?.fileSignedId;

    pushFile(newSignedId, {
      fileName: file.name,
      size: file.size,
      contentType: file.type,
    });

    await uploadFile(file, uploadDetails);
    setFileUploadState(newSignedId, "uploaded");

    methods.setValue(
      `extraAttachments.${fields.length - 1}.fileSignedId`,
      newSignedId
    );
  };

  return (
    <div className="flex flex-col gap-sp-md">
      {fields.map(({ id }, index) => (
        <Controller
          name={`extraAttachments.${index}.fileSignedId`}
          key={id}
          render={({ field, fieldState }) => (
            <KdsDropfile
              dataCy={`extraAttachmentsUpload-${index}`}
              errorMessage={getDropfileErrorMessage(fieldState.error?.message)}
              hasError={!!fieldState.error}
              isDeletable={!!field.value}
              isLoading={isLoading === index}
              isReadable={!!field.value}
              maxSize={SIZE_5G}
              onDelete={() => {
                if (fields.length > 1) {
                  remove(index);
                }
              }}
              onUploadStart={() => {
                setIsLoading(index);
                methods.clearErrors(`extraAttachments.${index}.fileSignedId`);
              }}
              onUpload={(file) => void handleUpload(file)}
              onUploadError={() => {
                methods.setError(`extraAttachments.${index}.fileSignedId`, {
                  type: "manual",
                  message: t(
                    "order.catalogMediumConfiguration.stepVersions.errorDropfile"
                  ),
                });
              }}
              onUploadSuccess={() => {
                methods.clearErrors(`extraAttachments.${index}.fileSignedId`);
                setIsLoading(false);
                append({ fileSignedId: "" });
              }}
              placeholderTitle={t("fileUpload.uploadInfo")}
              placeholderDescription={t(
                "order.catalogMediumConfiguration.stepVersions.uploadSize"
              )}
            />
          )}
        />
      ))}
    </div>
  );
};
